import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Check, Zap, Crown, Building2, ArrowRight, X } from 'lucide-react';
import axios from 'axios';
import AuthHeader from '../components/AuthHeader';
import AuthFooter from '../components/AuthFooter';
import authService from '../utils/authService';

const AllPlansProfessional = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedBillingCycle, setSelectedBillingCycle] = useState('monthly');
  const [processingPayment, setProcessingPayment] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

  const plans = {
    starter: {
      name: 'Starter',
      icon: <Zap className="w-12 h-12 text-blue-500" />,
      description: 'Perfect for small businesses & startups',
      price: 2999,
      color: 'blue',
      features: [
        'AI-powered SEO automation (Search Console + GA4)',
        'Automatic Google indexing & sitemap submission',
        'Core Web Vitals & PageSpeed monitoring',
        'Answer Engine Optimization (AEO)',
        'GEO / AI-search visibility optimization',
        'Weekly automated SEO reports',
        'Email & WhatsApp delivery'
      ]
    },
    pro: {
      name: 'Pro',
      icon: <Crown className="w-12 h-12 text-purple-500" />,
      description: 'Best for growing brands & e-commerce',
      price: 3999,
      color: 'purple',
      popular: true,
      features: [
        'Everything in Starter, plus:',
        '2 professional AI images weekly (DALL-E 3)',
        '2 cinematic AI videos weekly (RunwayML Gen-3)',
        '1 SEO-optimized blog article (1500+ words)',
        'Advanced analytics & insights dashboard',
        'Priority 24/7 support via chat & email',
        'Multi-location management (up to 3)',
        'Custom branding options'
      ]
    },
    advanced: {
      name: 'Advanced',
      icon: <Building2 className="w-12 h-12 text-green-500" />,
      description: 'For enterprises & agencies',
      price: 4999,
      color: 'green',
      features: [
        'Everything in Pro, plus:',
        'Unlimited AI-generated content',
        'White-label dashboard',
        'API access for integrations',
        'Dedicated account manager',
        'Custom AI model training',
        'Multi-brand management (unlimited)',
        'Priority enterprise support',
        'Advanced automation workflows'
      ]
    }
  };

  const billingCycles = [
    { value: 'monthly', label: 'Monthly', months: 1, discount: 0 },
    { value: 'quarterly', label: 'Quarterly (3 months)', months: 3, discount: 5 },
    { value: 'half_yearly', label: 'Half-Yearly (6 months)', months: 6, discount: 10 },
    { value: 'yearly', label: 'Yearly (12 months)', months: 12, discount: 15 }
  ];

  const calculatePrice = (basePrice, cycle) => {
    const billingCycle = billingCycles.find(c => c.value === cycle);
    const months = billingCycle.months;
    const discount = billingCycle.discount;
    
    const subtotal = basePrice * months;
    const discountAmount = (subtotal * discount) / 100;
    const afterDiscount = subtotal - discountAmount;
    const gst = afterDiscount * 0.18;
    const total = afterDiscount + gst;

    return {
      basePrice,
      months,
      subtotal,
      discount,
      discountAmount,
      afterDiscount,
      gst,
      total
    };
  };

  const handleSelectPlan = (planKey) => {
    setSelectedPlan(planKey);
    setSelectedBillingCycle('monthly');
    setShowBillingModal(true);
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleProceedToPayment = async () => {
    setProcessingPayment(true);

    try {
      // Get token from localStorage with ALL possible fallbacks
      let token = authService.getToken() || 
                  authService.getToken();
      
      // Fallback 1: Check auth object
      if (!token) {
        const authData = localStorage.getItem('auth');
        if (authData) {
          try {
            const parsed = JSON.parse(authData);
            token = parsed.token || parsed.authToken;
          } catch (e) {
            console.error('Failed to parse auth:', e);
          }
        }
      }

      // Fallback 2: Check user object
      if (!token) {
        const userData = localStorage.getItem('user');
        if (userData) {
          try {
            const parsed = JSON.parse(userData);
            token = parsed.token || parsed.authToken;
          } catch (e) {
            console.error('Failed to parse user:', e);
          }
        }
      }

      console.log('Token found:', token ? 'YES' : 'NO');

      if (!token) {
        console.error('No token found in any location');
        toast.error('Session expired. Please login again.');
        setShowBillingModal(false);
        setProcessingPayment(false);
        navigate('/login');
        return;
      }

      // Load Razorpay
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error('Failed to load payment gateway. Please refresh the page.');
        setProcessingPayment(false);
        return;
      }

      // Create order
      console.log('Creating order with token:', token.substring(0, 20) + '...');
      console.log('Plan:', selectedPlan, 'Cycle:', selectedBillingCycle);
      
      const orderResponse = await axios.post(
        `${API_BASE_URL}/api/subscription/create-order`,
        {
          plan_type: selectedPlan,
          billing_cycle: selectedBillingCycle
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('Order created successfully:', orderResponse.data);
      const { order_id, amount, currency, key_id } = orderResponse.data;

      // Close modal
      setShowBillingModal(false);

      // Open Razorpay
      const options = {
        key: key_id,
        amount: Math.round(amount * 100),
        currency: currency,
        name: 'AutomateSEO.ai',
        description: `${plans[selectedPlan].name} Plan - ${billingCycles.find(c => c.value === selectedBillingCycle).label}`,
        order_id: order_id,
        handler: async function (response) {
          try {
            const verifyResponse = await axios.post(
              `${API_BASE_URL}/api/subscription/verify-payment`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                plan_type: selectedPlan,
                billing_cycle: selectedBillingCycle
              },
              {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              }
            );

            if (verifyResponse.data) {
              toast.success('Payment successful! Subscription activated.');
              setTimeout(() => {
                navigate('/onboarding');
              }, 1500);
            }
          } catch (error) {
            console.error('Payment verification failed:', error);
            toast.error('Payment verification failed. Please contact support.');
          }
        },
        theme: {
          color: '#3B82F6'
        },
        modal: {
          ondismiss: function() {
            setProcessingPayment(false);
            toast.info('Payment cancelled');
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
      setProcessingPayment(false);

    } catch (error) {
      console.error('Payment error:', error);
      console.error('Error response:', error.response);
      
      let errorMsg = 'Failed to initiate payment. Please try again.';
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMsg = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          errorMsg = error.response.data.detail.map(e => e.msg || e).join(', ');
        }
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      toast.error(errorMsg);
      setProcessingPayment(false);
      setShowBillingModal(false);
    }
  };

  const currentPricing = selectedPlan ? calculatePrice(plans[selectedPlan].price, selectedBillingCycle) : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex flex-col">
      <AuthHeader />
      <div className="flex-1 py-12 px-4 pt-24 pb-24">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Choose Your Plan
            </h1>
            <p className="text-xl text-gray-600">
              Select the perfect plan for your business
            </p>
          </div>

          {/* Plans Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {Object.entries(plans).map(([key, plan]) => (
              <Card 
                key={key}
                className={`relative hover:shadow-2xl transition-all duration-300 ${
                  plan.popular ? 'border-4 border-purple-500 scale-105' : 'border-2 border-gray-200'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-2 text-sm font-bold shadow-lg">
                      ⭐ MOST POPULAR
                    </Badge>
                  </div>
                )}

                <CardHeader className="text-center space-y-4 pb-6 pt-8">
                  <div className="flex justify-center mb-4">
                    <div className={`p-4 rounded-2xl ${
                      plan.color === 'blue' ? 'bg-blue-100' :
                      plan.color === 'purple' ? 'bg-purple-100' :
                      'bg-green-100'
                    }`}>
                      {plan.icon}
                    </div>
                  </div>
                  
                  <CardTitle className="text-3xl font-bold">
                    {plan.name}
                  </CardTitle>
                  
                  <CardDescription className="text-base">
                    {plan.description}
                  </CardDescription>
                  
                  <div className="space-y-2 pt-4">
                    <div className="text-5xl font-bold text-gray-900">
                      ₹{plan.price.toLocaleString('en-IN')}
                    </div>
                    <div className="text-sm text-gray-500">/month</div>
                    <div className="text-xs text-gray-500">+ GST (18%)</div>
                  </div>
                </CardHeader>

                <CardContent className="space-y-4 px-6 pb-6">
                  <div className="space-y-3">
                    {plan.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>

                <CardFooter className="px-6 pb-6">
                  <Button
                    onClick={() => handleSelectPlan(key)}
                    className={`w-full py-6 text-lg font-semibold ${
                      plan.color === 'purple'
                        ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700'
                        : plan.color === 'blue'
                        ? 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700'
                        : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700'
                    } text-white shadow-lg`}
                  >
                    Get Started <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>

          {/* Info Section */}
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <h3 className="text-2xl font-bold mb-4">All Plans Include</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div>
                <div className="text-3xl mb-2">🔒</div>
                <p className="font-semibold">Secure Payments</p>
                <p className="text-sm text-gray-600">Via Razorpay</p>
              </div>
              <div>
                <div className="text-3xl mb-2">📱</div>
                <p className="font-semibold">24/7 Support</p>
                <p className="text-sm text-gray-600">Always here for you</p>
              </div>
              <div>
                <div className="text-3xl mb-2">✨</div>
                <p className="font-semibold">Regular Updates</p>
                <p className="text-sm text-gray-600">New features monthly</p>
              </div>
              <div>
                <div className="text-3xl mb-2">💯</div>
                <p className="font-semibold">Money-back Guarantee</p>
                <p className="text-sm text-gray-600">3-day refund policy</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Billing Cycle Selection Modal */}
      {showBillingModal && selectedPlan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <CardHeader className="border-b">
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-2xl">Select Billing Cycle</CardTitle>
                  <CardDescription className="mt-2">
                    Choose your preferred payment schedule for {plans[selectedPlan].name} Plan
                  </CardDescription>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setShowBillingModal(false)}
                  className="rounded-full"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </CardHeader>

            <CardContent className="space-y-4 pt-6">
              {billingCycles.map((cycle) => {
                const pricing = calculatePrice(plans[selectedPlan].price, cycle.value);
                return (
                  <div
                    key={cycle.value}
                    onClick={() => setSelectedBillingCycle(cycle.value)}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      selectedBillingCycle === cycle.value
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <input
                            type="radio"
                            checked={selectedBillingCycle === cycle.value}
                            onChange={() => setSelectedBillingCycle(cycle.value)}
                            className="w-4 h-4"
                          />
                          <div>
                            <p className="font-semibold text-lg">{cycle.label}</p>
                            {cycle.discount > 0 && (
                              <Badge className="bg-green-100 text-green-700 mt-1">
                                Save {cycle.discount}% (₹{Math.round(pricing.discountAmount).toLocaleString('en-IN')})
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold">₹{Math.round(pricing.afterDiscount).toLocaleString('en-IN')}</p>
                        <p className="text-sm text-gray-500">for {cycle.months} month{cycle.months > 1 ? 's' : ''}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          ₹{Math.round(pricing.afterDiscount / cycle.months).toLocaleString('en-IN')}/month
                        </p>
                        <p className="text-xs text-gray-600 mt-1">+ GST</p>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Price Breakdown */}
              {currentPricing && (
                <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mt-6">
                  <h4 className="font-semibold mb-3 text-gray-900">Final Payment Breakdown</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-700">Subscription ({currentPricing.months} month{currentPricing.months > 1 ? 's' : ''})</span>
                      <span className="font-semibold">₹{Math.round(currentPricing.subtotal).toLocaleString('en-IN')}</span>
                    </div>
                    {currentPricing.discount > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span>Discount ({currentPricing.discount}%)</span>
                        <span className="font-semibold">-₹{Math.round(currentPricing.discountAmount).toLocaleString('en-IN')}</span>
                      </div>
                    )}
                    <div className="flex justify-between border-t pt-2">
                      <span className="text-gray-700">Subtotal</span>
                      <span className="font-semibold">₹{Math.round(currentPricing.afterDiscount).toLocaleString('en-IN')}</span>
                    </div>
                    <div className="flex justify-between text-gray-700">
                      <span>GST (18%)</span>
                      <span className="font-semibold">₹{Math.round(currentPricing.gst).toLocaleString('en-IN')}</span>
                    </div>
                    <div className="border-t-2 border-blue-300 pt-2 flex justify-between font-bold text-lg text-blue-900">
                      <span>Total to Pay</span>
                      <span>₹{Math.round(currentPricing.total).toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>

            <CardFooter className="border-t pt-6">
              <Button
                onClick={handleProceedToPayment}
                disabled={processingPayment}
                className="w-full py-6 text-lg font-semibold bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
              >
                {processingPayment ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Processing...
                  </div>
                ) : (
                  `Proceed to Payment - ₹${currentPricing ? Math.round(currentPricing.total).toLocaleString('en-IN') : '0'}`
                )}
              </Button>
            </CardFooter>
          </Card>
        </div>
      )}

      <AuthFooter />
    </div>
  );
};

export default AllPlansProfessional;
